import type { PageName } from '~/config'
import type { Controller } from '~/features/controller'
import { AppUserService } from './appUserService'
import { AproDataService } from './aproDataService'
import { InspectionService } from './inspectionService'
import { InsuranceCompanyService } from './insuranceCompanyService'
import { LockService } from './lock/lockService'
import { EmployeeUserService } from './employeeUserService'
import { MedicationAgreementService } from './medicationAgreementService'
import { MedicineArticleService } from './medicineArticleService'
import { ParallelOrderService } from './parallelOrderService'
import { PrescriptionLineService } from './prescriptionLineService'
import { TaskService } from './taskService'
import { DealService } from './dealService'
import { ParcelService } from './parcelService'
import { PrescriptionLineTotalAmountInfoService } from './prescriptionLine/prescriptionLineTotalAmountInfoService'
import { GdsPlanningService } from './gdsPlanning/gdsPlanningService'
import { GdsPlanningOccurrenceService } from './gdsPlanning/gdsPlanningOccurrenceService'
import { MedicineProductService } from './medicineProductService'
import { MedicineService } from './medicineService'
import { MedicationAgreementMutationRequestService } from './medicationAgreementMutationRequestService'
import { ExternalUserDataPullService } from './externalUserDataPullService'
import { MedicineServicePull } from './medicineServicePull'
import * as Sentry from '@sentry/vue'
import { AppUserServicePull } from './appUserServicePull'
import { MedicationAgreementServicePull } from './medicationAgreementServicePull'
import { DeliveryItemService } from './deliveryItemService'
import { StockService } from './stockService'
import { UsageDatailService } from './usageDetailService'
import { WciaServicePull } from './wciaServicePull'
import { AdministrationAgreementPullService } from './administrationAgreementPullService'
import { MedicineArticlePullService } from './medicineArticlePullService'
import { MutationRequestsCountService } from './mutationRequestsCountService'
import { PlanningAdjustmentService } from './planningAdjustmentService'
import { PlanningAdjustmentsCountService } from './planningAdjustmentsCountService'
import { PrescriptionLineServicePaginated } from '~/services/prescriptionLineServicePaginated'
import { TaskServicePaginated } from '~/services/taskServicePaginated'
import { DealsCountService } from './dealsCountService'
import { InvoiceService } from './invoiceService'
import { InvoiceLineService } from './invoiceLineService'
import { VerifiedBsnPullService } from './verifiedBsnPullService'

export class CommunicationManagerContainer {
  protected lockService?: LockService
  protected appUserService?: AppUserService
  protected aproDataService?: AproDataService
  protected inspectionService?: InspectionService
  protected medAgreementService?: MedicationAgreementService
  protected prescriptionLineService?: PrescriptionLineService
  protected taskService?: TaskService
  protected medicineArticleService?: MedicineArticleService
  protected insuranceCompanyService?: InsuranceCompanyService
  protected employeeUserService?: EmployeeUserService
  protected parallelOrderService?: ParallelOrderService
  protected prescriptionLineTotalAmountInfoService?: PrescriptionLineTotalAmountInfoService
  protected dealService?: DealService
  protected parcelService?: ParcelService
  protected gdsPlanningService?: GdsPlanningService
  protected gdsPlanningOccurrenceService?: GdsPlanningOccurrenceService
  protected medicineProductService?: MedicineProductService
  protected medicineService?: MedicineService
  protected medicationAgreementMutationRequestService?: MedicationAgreementMutationRequestService
  protected externalUserDataService?: ExternalUserDataPullService
  protected medicineServicePull?: MedicineServicePull
  protected appUserServicePull?: AppUserServicePull
  protected medicationAgreementServicePull?: MedicationAgreementServicePull
  protected deliveryItemService?: DeliveryItemService
  protected stockService?: StockService
  protected usageDetailService?: UsageDatailService
  protected wciaServicePull?: WciaServicePull
  protected mutationRequestsCountService?: MutationRequestsCountService
  protected planningAdjustmentService?: PlanningAdjustmentService
  protected planningAdjustmentsCountService?: PlanningAdjustmentsCountService
  protected administrationAgreementPullService?: AdministrationAgreementPullService
  protected medicineArticlePullService?: MedicineArticlePullService
  protected prescriptionLineServicePaginated?: PrescriptionLineServicePaginated
  protected taskServicePaginated?: TaskServicePaginated
  protected dealsCountService?: DealsCountService
  protected invoiceService?: InvoiceService
  protected invoiceLineService?: InvoiceLineService
  protected verifiedBsnPullService?: VerifiedBsnPullService

  private masterControllers: Map<string, Controller> = new Map()
  private controllers: Controller[] = []

  public get LockService() {
    if (!this.lockService) {
      this.lockService = new LockService()
    }
    return this.lockService
  }

  public get AppUserService() {
    if (!this.appUserService) {
      this.appUserService = new AppUserService()
    }
    return this.appUserService
  }

  public get AproDataService() {
    if (!this.aproDataService) {
      this.aproDataService = new AproDataService()
    }
    return this.aproDataService
  }

  public get InspectionService() {
    if (!this.inspectionService) {
      this.inspectionService = new InspectionService()
    }
    return this.inspectionService
  }

  public get MedicationAgreementService() {
    if (!this.medAgreementService) {
      this.medAgreementService = new MedicationAgreementService()
    }
    return this.medAgreementService
  }

  public get PrescriptionLineService() {
    if (!this.prescriptionLineService) {
      this.prescriptionLineService = new PrescriptionLineService()
    }
    return this.prescriptionLineService
  }

  public get TaskService() {
    if (!this.taskService) {
      this.taskService = new TaskService()
    }
    return this.taskService
  }

  public get MedicineArticleService() {
    if (!this.medicineArticleService) {
      this.medicineArticleService = new MedicineArticleService()
    }
    return this.medicineArticleService
  }

  public get InsuranceCompanyService() {
    if (!this.insuranceCompanyService) {
      this.insuranceCompanyService = new InsuranceCompanyService()
    }
    return this.insuranceCompanyService
  }

  public get EmployeeUserService() {
    if (!this.employeeUserService) {
      this.employeeUserService = new EmployeeUserService()
    }
    return this.employeeUserService
  }

  public get ParallelOrderService() {
    if (!this.parallelOrderService) {
      this.parallelOrderService = new ParallelOrderService()
    }
    return this.parallelOrderService
  }

  public get DealService() {
    if (!this.dealService) {
      this.dealService = new DealService()
    }
    return this.dealService
  }

  public get PrescriptionLineTotalAmountInfoService() {
    if (!this.prescriptionLineTotalAmountInfoService) {
      this.prescriptionLineTotalAmountInfoService
        = new PrescriptionLineTotalAmountInfoService()
    }
    return this.prescriptionLineTotalAmountInfoService
  }

  public get ParcelService() {
    if (!this.parcelService) {
      this.parcelService = new ParcelService()
    }
    return this.parcelService
  }

  public get GdsPlanningService() {
    if (!this.gdsPlanningService) {
      this.gdsPlanningService = new GdsPlanningService()
    }
    return this.gdsPlanningService
  }

  public get GdsPlanningOccurrenceService() {
    if (!this.gdsPlanningOccurrenceService) {
      this.gdsPlanningOccurrenceService = new GdsPlanningOccurrenceService()
    }
    return this.gdsPlanningOccurrenceService
  }

  public get MedicineProductService() {
    if (!this.medicineProductService) {
      this.medicineProductService = new MedicineProductService()
    }
    return this.medicineProductService
  }

  public get MedicineService() {
    if (!this.medicineService) {
      this.medicineService = new MedicineService()
    }
    return this.medicineService
  }

  public get MedicationAgreementMutationRequestService() {
    if (!this.medicationAgreementMutationRequestService) {
      this.medicationAgreementMutationRequestService
        = new MedicationAgreementMutationRequestService()
    }
    return this.medicationAgreementMutationRequestService
  }

  public get MutationRequestsCountService() {
    if (!this.mutationRequestsCountService) {
      this.mutationRequestsCountService = new MutationRequestsCountService()
    }
    return this.mutationRequestsCountService
  }

  public get PlanningAdjustmentService() {
    this.planningAdjustmentService ??= new PlanningAdjustmentService()
    return this.planningAdjustmentService
  }

  public get PlanningAdjustmentsCountService() {
    this.planningAdjustmentsCountService ??= new PlanningAdjustmentsCountService()
    return this.planningAdjustmentsCountService
  }

  public get DealsCountService() {
    if (!this.dealsCountService) {
      this.dealsCountService = new DealsCountService()
    }
    return this.dealsCountService
  }

  public get ExternalUserDataService() {
    if (!this.externalUserDataService) {
      this.externalUserDataService = new ExternalUserDataPullService()
    }
    return this.externalUserDataService
  }

  public get MedicineServicePull() {
    if (!this.medicineServicePull) {
      this.medicineServicePull = new MedicineServicePull()
    }
    return this.medicineServicePull
  }

  public get AppUserServicePull() {
    if (!this.appUserServicePull) {
      this.appUserServicePull = new AppUserServicePull()
    }
    return this.appUserServicePull
  }

  public get MedicationAgreementServicePull() {
    if (!this.medicationAgreementServicePull) {
      this.medicationAgreementServicePull = new MedicationAgreementServicePull()
    }
    return this.medicationAgreementServicePull
  }

  public get DeliveryItemService() {
    if (!this.deliveryItemService) {
      this.deliveryItemService = new DeliveryItemService()
    }
    return this.deliveryItemService
  }

  public get StockService() {
    if (!this.stockService) {
      this.stockService = new StockService()
    }
    return this.stockService
  }

  public get UsageDetailService() {
    if (!this.usageDetailService) {
      this.usageDetailService = new UsageDatailService()
    }
    return this.usageDetailService
  }

  public get WciaServicePull() {
    if (!this.wciaServicePull) {
      this.wciaServicePull = new WciaServicePull()
    }
    return this.wciaServicePull
  }

  public get AdministrationAgreementPullService() {
    if (!this.administrationAgreementPullService) {
      this.administrationAgreementPullService
        = new AdministrationAgreementPullService()
    }
    return this.administrationAgreementPullService
  }

  public get MedicineArticlePullService() {
    if (!this.medicineArticlePullService) {
      this.medicineArticlePullService = new MedicineArticlePullService()
    }
    return this.medicineArticlePullService
  }

  public get PrescriptionLineServicePaginated() {
    if (!this.prescriptionLineServicePaginated) {
      this.prescriptionLineServicePaginated
        = new PrescriptionLineServicePaginated()
    }
    return this.prescriptionLineServicePaginated
  }

  public get InvoiceService() {
    if (!this.invoiceService) {
      this.invoiceService = new InvoiceService()
    }
    return this.invoiceService
  }

  public get InvoiceLineService() {
    if (!this.invoiceLineService) {
      this.invoiceLineService = new InvoiceLineService()
    }
    return this.invoiceLineService
  }

  public get VerifiedBsnPullService() {
    if (!this.verifiedBsnPullService) {
      this.verifiedBsnPullService = new VerifiedBsnPullService()
    }
    return this.verifiedBsnPullService
  }

  public addController(controller: Controller) {
    if (controller.isMaster) {
      this.masterControllers.set(controller.pageName, controller)
    }

    this.controllers.push(controller)
  }

  public get TaskServicePaginated() {
    if (!this.taskServicePaginated) {
      this.taskServicePaginated = new TaskServicePaginated()
    }
    return this.taskServicePaginated
  }

  public getMasterController<T extends Controller>(controller: Controller): T {
    const masterController = this.masterControllers.get(controller.pageName)
    if (!masterController) {
      throw new Error(`Master not found for controller`)
    }
    return masterController as T
  }

  public activatePage(pageName: PageName) {
    const services = [
      this.LockService,
      this.AppUserService,
      this.AproDataService,
      this.InspectionService,
      this.MedicationAgreementService,
      this.PrescriptionLineService,
      this.TaskService,
      this.MedicineArticleService,
      this.InsuranceCompanyService,
      this.EmployeeUserService,
      this.ParallelOrderService,
      this.DealService,
      this.PrescriptionLineTotalAmountInfoService,
      this.ParcelService,
      this.GdsPlanningService,
      this.GdsPlanningOccurrenceService,
      this.MedicineProductService,
      this.MedicineService,
      this.MedicationAgreementMutationRequestService,
      this.ExternalUserDataService,
      this.MedicineServicePull,
      this.AppUserServicePull,
      this.MedicationAgreementServicePull,
      this.DeliveryItemService,
      this.StockService,
      this.UsageDetailService,
      this.WciaServicePull,
      this.MutationRequestsCountService,
      this.PlanningAdjustmentService,
      this.PlanningAdjustmentsCountService,
      this.AdministrationAgreementPullService,
      this.MedicineArticlePullService,
      this.PrescriptionLineServicePaginated,
      this.TaskServicePaginated,
      this.DealsCountService,
      this.InvoiceService,
      this.InvoiceLineService,
      this.VerifiedBsnPullService,
    ]

    Sentry.startSpan({ name: `activatePage-${pageName}` }, () => {
      const span = Sentry.getActiveSpan()
      const startTime = performance.now()

      for (const service of services) {
        service.activatePage(pageName)
      }

      for (const controller of this.controllers) {
        controller.initializeController()
      }

      span?.setAttribute('duration', (performance.now() - startTime).toString())
    })
  }
}

export const CommunicationManager = new CommunicationManagerContainer()
